<script setup>
import moment from 'moment';
import { ref } from 'vue';

const emits = defineEmits(['update:modelValue', 'on-blur']);

const props = defineProps({
  name: String,
  autocomplete: String,
  placeholder: {
    type: String,
    default: 'MM/DD/YYYY',
  },
  label: String,
  density: String,
  variant: String,
  class: String,
  errorMessages: Array,
  disabled: Boolean,
  rounded: String,
  hideDetails: [Boolean, String],
  modelValue: [Date, String],
  useCurrentInitialPage: {
    type: Boolean,
    default: false,
  },
});

const START_YEAR = moment().subtract(20, 'years').year();

/**
 * Format date.
 * @param {Date} date Date to format.
 * @returns {string}
 */
function toDateAsString(date) {
  return moment(date).format('MM/DD/YYYY');
}

/**
 * Check date valid or not.
 * @param {string} stringDateToParse Date to check.
 * @returns {moment.Moment}
 */
function parseMoment(stringDateToParse) {
  return moment(stringDateToParse, 'MM/DD/YYYY', true);
}

const popover = ref({
  visibility: 'click',
  placement: 'bottom-end',
});

const inputValue = ref(props.modelValue ? toDateAsString(props.modelValue) : '');

/**
 * Handle update model value.
 * @param {Date} newValue New value.
 */
function onUpdateModelValue(newValue) {
  emits('update:modelValue', newValue);
  if (newValue == null) {
    inputValue.value = '';
  }
  inputValue.value = toDateAsString(newValue);
}

/** Handle input blur. */
function onInputBlur() {
  const parsedInputValue = parseMoment(inputValue.value);
  if (parsedInputValue.isValid() || inputValue.value == null || inputValue.value === '') {
    emits('on-blur');
    return;
  }
  inputValue.value = toDateAsString(props.modelValue);
  emits('on-blur');
}

/**
 * Handle input change.
 * @param {string} newValue New input value.
 */
function onInputChange(newValue) {
  if (newValue === '') {
    emits('update:modelValue', null);
  }
  const parsedMoment = parseMoment(newValue);
  if (parsedMoment.isValid()) {
    emits('update:modelValue', parsedMoment.toDate());
  }
}

</script>

<template>
  <vue-date-picker
    :model-value="modelValue"
    :popover="popover"
    :initial-page="useCurrentInitialPage ? { month: moment().month() + 1, year: moment().year() } : { month: 1, year: START_YEAR }"
    @update:model-value="onUpdateModelValue"
  >
    <template #default="{ togglePopover }">
      <v-text-field
        v-model="inputValue"
        v-mask="'##/##/####'"
        :disabled="disabled"
        :name="name"
        :label="label"
        :class="class"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :density="density"
        :variant="variant"
        :rounded="rounded"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
        @update:model-value="onInputChange"
        @blur="onInputBlur"
        @click="togglePopover"
      >
        <template #append-inner>
          <button>
            <v-icon size="small" icon="fas fa-calendar" />
          </button>
        </template>
        <template v-if="$slots.label" #label>
          <slot name="label" />
        </template>
      </v-text-field>
    </template>
  </vue-date-picker>
</template>
